import { useState, useEffect } from "react";
import { AuthLayout } from "./authlayout";
import { TextField } from "../common/inputFiled";
import { PhoneField } from "../common/phoneFiled";
import toast from "react-hot-toast";
import { CountryModal } from "../../auth/countrymodal";
import { appStorage } from "../../utils/appStorage";

const NAME_PATTERN = /^[a-zA-Z\s']+$/;
const NAME_INPUT_FILTER = /[^a-zA-Z\s']/g;
const NAME_ERROR = "Name can only contain letters, spaces, and apostrophes.";
const FIRST_NAME_PATTERN = NAME_PATTERN;
const FIRST_NAME_ERROR = NAME_ERROR;

const sanitizeNameInput = (value) => {
  const cleaned = String(value || "").replace(/^\s+/, "").replace(NAME_INPUT_FILTER, "");
  if (!cleaned) return "";
  return cleaned
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ");
};

const isValidName = (value) =>
  Boolean(value?.trim()) && NAME_PATTERN.test(value.trim()) && /[a-zA-Z]/.test(value);

export default function SignUp () {
  const [showCountryModal, setShowCountryModal] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    invite: "",
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const getDeviceName = () => {
    const userAgent = navigator.userAgent || "";
    const platform = navigator.userAgentData?.platform || navigator.platform || "Unknown device";

    if (/Android/i.test(userAgent)) return "Android";
    if (/iPhone|iPad|iPod/i.test(userAgent)) return "iPhone/iPad";
    if (/Windows/i.test(userAgent)) return "Windows PC";
    if (/Macintosh|Mac OS X/i.test(userAgent)) return "Mac";
    if (/Linux/i.test(userAgent)) return "Linux";

    return platform;
  };

  const getClientIpAddress = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (!response.ok) return "";

      const data = await response.json();
      return data?.ip || "";
    } catch (error) {
      return "";
    }
  };

  // Restore saved form + invitation code
  useEffect(() => {
    // try {
    //   const saved = localStorage.getItem("signupForm");
    //   if (saved) {
    //     setFormData((prev) => ({
    //       ...prev,
    //       ...JSON.parse(saved),
    //     }));
    //   }
    // } catch (err) {
    //   // }

    const params = new URLSearchParams(window.location.search);
    const inviteParam = params.get("invite") || params.get("invitation_code") || params.get("invite_code");
    if (inviteParam) {
      setFormData((prev) => ({ ...prev, invite: inviteParam }));
    }
  }, []);

  // Save form data after every change
  useEffect(() => {
    localStorage.setItem("signupForm", JSON.stringify(formData));
  }, [formData]);

  // Generic input + phone data handler
  const handleChange = (e) => {
    const { name, value, type, checked, countryCode, phoneNumber } = e.target;

    // compute new form state synchronously so we can validate immediately
    let newVal = type === "checkbox" ? checked : value;

    if (name === "firstName" || name === "lastName") {
      newVal = sanitizeNameInput(newVal);
    }

    // Disallow special characters in the invite code field (allow letters, numbers, -, _, .)
    if (name === "invite") {
      const sanitized = String(newVal || "").replace(/[^a-zA-Z0-9\-_.]/g, "");
      newVal = sanitized;
    }
    const updated = {
      ...formData,
      [name]: newVal,
      ...(countryCode && { countryCode }),
      ...(phoneNumber && { phoneNumber }),
    };

    setFormData(updated);

    // clear field-specific error for the edited field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Live password/confirmPassword matching:
    if (name === "password" || name === "confirmPassword") {
      const pass = updated.password || "";
      const confirm = updated.confirmPassword || "";

      // if user has typed anything in confirm field, validate live
      if (confirm.length > 0) {
        if (pass !== confirm) {
          setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
        } else {
          setErrors((prev) => {
            const copy = { ...prev };
            delete copy.confirmPassword;
            return copy;
          });
        }
      } else {
        // if confirm is empty, remove the confirmPassword error
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy.confirmPassword;
          return copy;
        });
      }
    }
  };

  // When country is selected in modal
  const handleCountrySelect = (country) => {
    // Save selected country in localStorage
    localStorage.setItem("selectedCountry", country.code);
    localStorage.setItem("selectedDialCode", `+${country.dialCode}`);

    setFormData((prev) => ({
      ...prev,
      countryCode: `+${country.dialCode}`,
      phone: country.dialCode, // auto-fill prefix
      phoneNumber: "",
    }));

    setShowCountryModal(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setErrors({ general: "Please fill in all required fields." });
      return;
    }

    if (!isValidName(formData.firstName)) {
      setErrors((prev) => ({ ...prev, firstName: NAME_ERROR }));
      return;
    }

    if (!isValidName(formData.lastName)) {
      setErrors((prev) => ({ ...prev, lastName: NAME_ERROR }));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      return;
    }

    if (!formData.countryCode || !formData.phoneNumber) {
      setErrors({ general: "Please enter a valid phone number." });
      return;
    }

    if (!formData.agreeToTerms) {
      setErrors({ general: "Please agree to the terms & privacy policy." });
      return;
    }

    setLoading(true);

    try {
      const [deviceName, ipAddress] = await Promise.all([
        Promise.resolve(getDeviceName()),
        getClientIpAddress(),
      ]);

      const payload = new URLSearchParams({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        country_code: formData.countryCode,
        phone_number: formData.phoneNumber,
        password: formData.password,
        deviceType: "Website",
        device_name: deviceName,
        ip_address: ipAddress,
      });
      if (formData.invite) {
        payload.append("invite", formData.invite);
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.dismiss();
        toast.error(data.message || "Signup failed");
        throw new Error(data.message || "Signup failed");
      }

      toast.success("Sign Up successfully!");

      // Save email in appStorage
      appStorage.setItem("email", formData.email);

      // Clear saved form
      localStorage.removeItem("signupForm");

      window.location.href = "/sign-up/otp";
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
      setErrors({ general: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleHaveInvitationCode = () => {
    const params = new URLSearchParams(window.location.search);
    const inviteParam = params.get("invite") || params.get("invitation_code") || params.get("invite_code") || "";
    const target = inviteParam
      ? `/invitation-code?invite=${encodeURIComponent(inviteParam)}`
      : "/invitation-code";
    window.location.href = target;
  };

  return (
    <>
      {showCountryModal && (
        <CountryModal
          onSelect={handleCountrySelect}
          onClose={() => setShowCountryModal(false)}
        />
      )}

      <AuthLayout
        title={
          <>
            JOIN <span className="text-[#643ECF]">PHONEBOOKPLUS</span>
          </>
        }
        subtitle="Manage contacts and chat in one place—link multiple numbers to a single profile and control exactly what your network sees."
      >
        <form className="space-y-4" onSubmit={handleSignUp}>
          <TextField
            id="firstName"
            type="text"
            name="firstName"
            label="First Name"
            required
            startIcon="./input-logos/user.svg"
            placeholder="Enter First Name"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
          />

          <TextField
            id="lastName"
            type="text"
            name="lastName"
            label="Last Name"
            required
            startIcon="./input-logos/user.svg"
            placeholder="Enter Last Name"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
          />

          <TextField
            id="email"
            type="email"
            name="email"
            label="Email"
            required
            startIcon="./input-logos/sms.svg"
            placeholder="Enter Email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />
          <PhoneField
            name="phone"
            label="Phone"
            required
            value={formData.phone}
            onChange={handleChange}
            error={errors.phone}
            openCountryModal={() => setShowCountryModal(true)}
          />
          <TextField
            id="password"
            type="password"
            name="password"
            label="Password"
            required
            startIcon="./input-logos/password.svg"
            placeholder="My Password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
          />
          <TextField
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            required
            startIcon="./input-logos/password.svg"
            placeholder="Confirm My Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
          />
          <TextField
            id="invite"
            type="text"
            name="invite"
            label="Invite Code"
            startIcon="./input-logos/user.svg"
            placeholder="Enter invite code or join waitlist"
            value={formData.invite}
            onChange={handleChange}
            error={errors.invite}
          />
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              name="agreeToTerms"    
              required
              className="h-4 w-4 accent-[#7C4DFF] border-gray-300"
              checked={formData.agreeToTerms}
              onChange={handleChange}
            />
            <span className="text-sm text-gray-700">
              I agree with{" "}
              <a href="/term-condition" className="text-[#7C4DFF] underline">
                terms & conditions
              </a>{" "}
              and{" "}
              <a href="/privacy-policy" className="text-[#7C4DFF] underline">
                privacy policy
              </a>
            </span>
          </label>

          {errors.general && (
            <p className="text-red-500 text-sm">{errors.general}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-50"
          >
            {loading ? "Signing Up..." : "Continue"}
          </button>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <a href="/login" className="text-[#7C4DFF] underline">
              Sign in here
            </a>
          </p>

          {/* <button
            type="button"
            onClick={handleHaveInvitationCode}
            className="w-full rounded-3xl border border-[#7C4DFF] py-3 text-[#6A35FF] font-semibold bg-gradient-to-b from-white to-[#F7F6FF] hover:from-[#F3F0FF] hover:to-[#F7F6FF] hover:shadow-sm hover:scale-[1.01] active:scale-[0.995] transform transition"
          >
            Have an invitation code?
          </button> */}
        </form>
      </AuthLayout>
    </>
  );
}

